import { useState, useEffect } from 'react'

export interface CurrencyInfo {
  code: string
  symbol: string
  rate: number // rate vs USD
}

export interface UseCurrencyReturn {
  loading: boolean
  currency: CurrencyInfo
  formatPrice: (usdAmount: number) => string
}

const USD_FALLBACK: CurrencyInfo = { code: 'USD', symbol: '$', rate: 1 }

// Common currency symbols
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CAD: 'CA$',
  AUD: 'A$',
  BRL: 'R$',
  MXN: 'MX$',
  ARS: 'AR$',
  CLP: 'CL$',
  COP: 'CO$',
  PEN: 'S/',
  UYU: '$U',
  PYG: '₲',
  BOB: 'Bs',
  VES: 'Bs.S',
  INR: '₹',
  CNY: '¥',
  KRW: '₩',
  SGD: 'S$',
  HKD: 'HK$',
  NZD: 'NZ$',
  CHF: 'CHF',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  CZK: 'Kč',
  HUF: 'Ft',
  RON: 'lei',
  BGN: 'лв',
  TRY: '₺',
  RUB: '₽',
  UAH: '₴',
  ZAR: 'R',
  EGP: 'E£',
  SAR: '﷼',
  AED: 'د.إ',
  ILS: '₪',
  THB: '฿',
  IDR: 'Rp',
  MYR: 'RM',
  PHP: '₱',
  VND: '₫',
  PKR: '₨',
  BDT: '৳',
}

export function useCurrency(): UseCurrencyReturn {
  const [loading, setLoading] = useState(true)
  const [currency, setCurrency] = useState<CurrencyInfo>(USD_FALLBACK)

  useEffect(() => {
    let cancelled = false

    async function detect() {
      try {
        // 1. Detect country/currency via IP
        const geoRes = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(5000),
        })
        if (!geoRes.ok) throw new Error('geo failed')
        const geo = await geoRes.json()
        const currencyCode: string = geo.currency || 'USD'

        if (currencyCode === 'USD') {
          if (!cancelled) {
            setCurrency(USD_FALLBACK)
            setLoading(false)
          }
          return
        }

        // 2. Fetch exchange rate (frankfurter.app is free & reliable)
        const rateRes = await fetch(
          `https://api.frankfurter.app/latest?from=USD&to=${currencyCode}`,
          { signal: AbortSignal.timeout(5000) },
        )
        if (!rateRes.ok) throw new Error('rate failed')
        const rateData = await rateRes.json()
        const rate: number = rateData.rates?.[currencyCode] ?? 1

        if (!cancelled) {
          setCurrency({
            code: currencyCode,
            symbol: CURRENCY_SYMBOLS[currencyCode] ?? currencyCode + ' ',
            rate,
          })
        }
      } catch {
        // Silently fall back to USD
        if (!cancelled) setCurrency(USD_FALLBACK)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    detect()
    return () => {
      cancelled = true
    }
  }, [])

  const formatPrice = (usdAmount: number): string => {
    const converted = usdAmount * currency.rate
    // Format based on currency conventions
    const isNoDecimal = [
      'JPY',
      'KRW',
      'VND',
      'IDR',
      'PYG',
      'HUF',
      'CLP',
    ].includes(currency.code)
    const formatted = isNoDecimal
      ? Math.round(converted).toLocaleString()
      : converted.toFixed(2)
    return `${currency.symbol}${formatted}`
  }

  return { loading, currency, formatPrice }
}
